/**
 * 공개 커뮤니티 API — 게시판 목록/상세, 게시글 CRUD, 댓글, 좋아요.
 *
 * 영구지침: docs/develop/community/board-cms.md §4.2, post.md §4.1
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import apiClient from '@/lib/api/client'

// =========================================================================
// 타입
// =========================================================================

export interface PublicBoardSummary {
  id: number
  slug: string
  name: string
  description: string | null
  displayOrder: number
  allowAnonymous: boolean
  allowFacilityTag: boolean
  enableAcceptedAnswer: boolean
  allowSecretPost: boolean
  allowImageAttachment: boolean
  allowFileAttachment: boolean
  postCount: number
  myPermissions: {
    canReadPost: boolean
    canWritePost: boolean
    canWriteComment: boolean
    canLike: boolean
    canReport: boolean
  }
}

export interface PublicBoardDetail extends PublicBoardSummary {
  active: boolean
  postTitleTemplate: string | null
  postContentTemplate: string | null
  postTemplateGuide: string | null
  createdAt: string
  updatedAt: string
}


export interface AuthorRef {
  id: number | null
  nickname: string
  profileImageUrl: string | null
}

export interface FacilityRef {
  id: number
  name: string
}

export interface BoardRef {
  id: number
  slug: string
  name: string
}

export type AttachmentKind = 'IMAGE' | 'FILE'

export interface AttachmentRef {
  id: number | null
  fileKey: string
  originalName: string
  contentType: string
  sizeBytes: number
  kind: AttachmentKind
  orderIndex: number
  createdAt: string | null
}

export interface PostSummary {
  id: number
  title: string
  excerpt: string
  author: AuthorRef
  facility: FacilityRef | null
  board: BoardRef | null
  viewCount: number
  likeCount: number
  commentCount: number
  isAnonymous: boolean
  isSecret: boolean
  isNotice: boolean
  isGlobalNotice: boolean
  hasAcceptedAnswer: boolean
  likedByMe: boolean
  attachmentCount: number
  createdAt: string
}

export interface PostDetail {
  id: number
  board: BoardRef
  author: AuthorRef
  facility: FacilityRef | null
  title: string
  content: string
  /** 'text' (기본 — 마크다운/일반 텍스트) | 'html' (외부 자동 수집글). 백엔드 PostResponse.contentFormat */
  contentFormat?: 'text' | 'html'
  viewCount: number
  likeCount: number
  commentCount: number
  isAnonymous: boolean
  isSecret: boolean
  isNotice: boolean
  isGlobalNotice: boolean
  acceptedAnswerId: number | null
  isHidden: boolean
  likedByMe: boolean
  editableByMe: boolean
  attachments: AttachmentRef[]
  createdAt: string
  updatedAt: string
  /** 외부 자동수집(KOGL 공공저작물 전재) 글 여부 — true면 noindex(색인 제외) */
  isSyndicated?: boolean
  /** 출처 기관명 — KOGL 출처표시 의무. 자동수집글에만 존재 */
  sourceAttribution?: string | null
  /** 원문 URL — 자동수집글에만 존재 */
  externalUrl?: string | null
}

export interface CommentItem {
  id: number
  postId: number
  parentId: number | null
  author: AuthorRef
  content: string
  likeCount: number
  isAnonymous: boolean
  isAccepted: boolean
  isHidden: boolean
  editableByMe: boolean
  createdAt: string
}

export interface PageResponse<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

export type SortKey = 'recent' | 'popular' | 'comments' | 'views'

// =========================================================================
// 게시판
// =========================================================================

export function useCommunityBoards() {
  return useQuery({
    queryKey: ['community-boards'],
    queryFn: async () => {
      const res = await apiClient.get('/community/boards')
      return res.data.data as PublicBoardSummary[]
    },
  })
}

export function useCommunityBoard(slug: string | undefined) {
  return useQuery({
    queryKey: ['community-board', slug],
    queryFn: async () => {
      const res = await apiClient.get(`/community/boards/${slug}`)
      return res.data.data as PublicBoardDetail
    },
    enabled: !!slug,
  })
}

// =========================================================================
// 게시글 목록 (무한 스크롤)
// =========================================================================

export function useBoardPosts(slug: string | undefined, sort: SortKey = 'recent', size = 20) {
  return useInfiniteQuery({
    queryKey: ['community-posts', slug, sort],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await apiClient.get(`/community/boards/${slug}/posts`, {
        params: { sort, page: pageParam, size },
      })
      return res.data.data as PageResponse<PostSummary>
    },
    initialPageParam: 0,
    getNextPageParam: (last) =>
      last && last.number + 1 < last.totalPages ? last.number + 1 : undefined,
    enabled: !!slug,
  })
}

// =========================================================================
// 게시글 상세 / 댓글
// =========================================================================

export function usePost(id: number | null) {
  return useQuery({
    queryKey: ['community-post', id],
    queryFn: async () => {
      const res = await apiClient.get(`/community/posts/${id}`)
      return res.data.data as PostDetail
    },
    enabled: id != null,
  })
}

export function usePostComments(id: number | null) {
  return useQuery({
    queryKey: ['community-post-comments', id],
    queryFn: async () => {
      const res = await apiClient.get(`/community/posts/${id}/comments`)
      return res.data.data as CommentItem[]
    },
    enabled: id != null,
  })
}

// =========================================================================
// 뮤테이션
// =========================================================================

export interface PostCreatePayload {
  title: string
  content: string
  facilityId?: number | null
  isAnonymous: boolean
  isSecret: boolean
  isNotice: boolean
  isGlobalNotice: boolean
  attachments?: Array<{
    fileKey: string
    originalName: string
    contentType: string
    sizeBytes: number
    kind: AttachmentKind
  }>
}

// =========================================================================
// 첨부 업로드
// =========================================================================

export async function uploadAttachment(
  slug: string,
  kind: AttachmentKind,
  file: File,
): Promise<AttachmentRef> {
  const form = new FormData()
  form.append('kind', kind)
  form.append('file', file)
  const res = await apiClient.post(`/community/boards/${slug}/attachments`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data.data as AttachmentRef
}

export function useCreatePost(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: PostCreatePayload) => {
      const res = await apiClient.post(`/community/boards/${slug}/posts`, data)
      return res.data.data as PostDetail
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['community-posts', slug] })
      qc.invalidateQueries({ queryKey: ['community-boards'] })
      qc.invalidateQueries({ queryKey: ['community-board', slug] })
    },
  })
}

export function useUpdatePost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: PostCreatePayload }) => {
      const res = await apiClient.patch(`/community/posts/${id}`, data)
      return res.data.data as PostDetail
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['community-post', vars.id] })
    },
  })
}

export function useDeletePost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/community/posts/${id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['community-posts'] })
      qc.invalidateQueries({ queryKey: ['community-post'] })
    },
  })
}

export function useToggleLike() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (postId: number) => {
      const res = await apiClient.post(`/community/posts/${postId}/like`)
      return res.data.data as { liked: boolean; likeCount: number }
    },
    onSuccess: (_d, postId) => {
      qc.invalidateQueries({ queryKey: ['community-post', postId] })
      qc.invalidateQueries({ queryKey: ['community-posts'] })
    },
  })
}

export interface CommentCreatePayload {
  content: string
  parentId?: number | null
  isAnonymous: boolean
}

export function useCreateComment(postId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: CommentCreatePayload) => {
      const res = await apiClient.post(`/community/posts/${postId}/comments`, data)
      return res.data.data as CommentItem
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['community-post-comments', postId] })
      qc.invalidateQueries({ queryKey: ['community-post', postId] })
    },
  })
}

export function useUpdateComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, content }: { id: number; content: string }) => {
      const res = await apiClient.patch(`/community/comments/${id}`, { content })
      return res.data.data as CommentItem
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['community-post-comments'] })
    },
  })
}

export function useDeleteComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/community/comments/${id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['community-post-comments'] })
      qc.invalidateQueries({ queryKey: ['community-post'] })
    },
  })
}

export function useAcceptAnswer(postId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (commentId: number) => {
      await apiClient.post(`/community/posts/${postId}/accept-answer`, { commentId })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['community-post', postId] })
      qc.invalidateQueries({ queryKey: ['community-post-comments', postId] })
    },
  })
}
